import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { beautify } from "../../utils/helpers";

type TModalItemsTableProps = {
  fields: any[];
  items: any[];
  detailsView: (item: any) => any;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  disabled?: boolean;
};

const formatValue = (field: any, value: any) => {
  if (value === null || value === undefined || value === "") return "—";

  // raw ids (e.g. records loaded from the API) -> option names
  if (field.type === "select" && Array.isArray(field.options_data)) {
    const option = field.options_data.find((o: any) => o.id === value);
    if (option) return option.name;
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (field.type === "number" && !isNaN(Number(value))) {
    return Number(value).toLocaleString();
  }

  return String(value);
};

export const ModalItemsTable = ({
  fields,
  items,
  detailsView,
  onEdit,
  onDelete,
  disabled,
}: TModalItemsTableProps) => {
  const columns = (fields || []).filter((f) => f.visible !== false);

  if (!items?.length) return null;

  return (
    <TableWrap>
      <CTable small bordered hover className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ width: 36 }}>#</CTableHeaderCell>
            {columns.map((col) => (
              <CTableHeaderCell key={col.field_name}>
                {col.label ?? beautify(col.field_name)}
              </CTableHeaderCell>
            ))}
            <CTableHeaderCell className="text-center" style={{ width: 76 }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {items.map((it, idx) => {
            const view = detailsView(it);
            return (
              <CTableRow key={idx}>
                <CTableDataCell>{idx + 1}</CTableDataCell>
                {columns.map((col) => (
                  <CTableDataCell key={col.field_name}>
                    {formatValue(col, view?.[col.field_name])}
                  </CTableDataCell>
                ))}
                <CTableDataCell className="text-center">
                  <IconButton
                    type="button"
                    aria-label="Edit"
                    disabled={disabled}
                    onClick={() => onEdit(idx)}
                  >
                    <EditOutlined />
                  </IconButton>
                  <IconButton
                    type="button"
                    aria-label="Delete"
                    disabled={disabled}
                    onClick={() => onDelete(idx)}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </CTableDataCell>
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>
    </TableWrap>
  );
};

const TableWrap = styled.div`
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;

  table {
    font-size: 12px;
  }

  th {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #64748b;
    background: #f8fafc;
    padding: 5px 10px;
    white-space: nowrap;
  }

  td {
    padding: 4px 10px;
    vertical-align: middle;
    color: #334155;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 2px 6px;
  font-size: 14px;
  color: #475569;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #1a75ba;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
